<template>
  <div class="vtx-page">
    <Loader v-if="loading" />

    <div class="vtx-container">
      <div class="vtx-switch-row">
        <div class="vtx-switch" role="tablist" aria-label="Categoria fornitura">
          <span class="vtx-switch-indicator" :class="{ 'vtx-switch-indicator--gas': category === 'GAS' }"></span>
          <button type="button" class="vtx-switch-btn" :class="{ 'vtx-switch-btn--active': category === 'EE' }"
            role="tab" :aria-selected="category === 'EE'" @click="setCategory('EE')">
            <i class="pi pi-bolt"></i>
            <span>EE</span>
          </button>
          <button type="button" class="vtx-switch-btn" :class="{ 'vtx-switch-btn--active': category === 'GAS' }"
            role="tab" :aria-selected="category === 'GAS'" @click="setCategory('GAS')">
            <i class="pi pi-fire"></i>
            <span>GAS</span>
          </button>
        </div>
      </div>

      <header class="vtx-header">
        <h1 class="vtx-title">{{ pageTitle }}</h1>
        <p class="vtx-subtitle">{{ pageSubtitle }}</p>
      </header>

      <section class="vtx-card">
        <h2 class="vtx-card-title">Caricamento file</h2>

        <label class="vtx-dropzone" :class="{ 'vtx-dropzone--drag': isDragging }" @dragover.prevent="onDragOver"
          @dragleave.prevent="onDragLeave" @drop.prevent="onDrop">
          <input type="file" ref="fileInput" class="vtx-dropzone-input" :accept="acceptedFileTypes" multiple
            @change="handleFileUpload" />
          <i class="pi pi-cloud-upload vtx-dropzone-icon"></i>
          <span class="vtx-dropzone-text">Trascina i file qui o clicca per selezionare</span>
          <span class="vtx-dropzone-hint">Formati consentiti: {{ allowedType.join(", ").toUpperCase() }}</span>
        </label>

        <div v-show="validationErrors.files && submitted" class="vtx-error">Seleziona almeno un file.</div>
        <div v-show="validationErrors.type && submitted" class="vtx-error">Formato non consentito.</div>

        <ul v-if="fileToUpload.length > 0" class="vtx-file-list">
          <li v-for="(file, index) in fileToUpload" :key="index" class="vtx-file-item">
            <i class="pi pi-file"></i>
            <span class="vtx-file-name">{{ file.name }}</span>
            <span class="vtx-file-size">{{ formatFileSize(file.size) }}</span>
            <button type="button" class="vtx-file-remove" aria-label="Rimuovi file" @click="removeFile(index)">
              <i class="pi pi-times"></i>
            </button>
          </li>
        </ul>

        <div class="vtx-actions">
          <button type="button" class="vtx-btn" :class="{ 'vtx-btn--gas': category === 'GAS' }" :disabled="loading"
            @click="uploadFile">
            <i class="pi pi-upload"></i>
            Carica file {{ category }}
          </button>
        </div>
      </section>

      <section class="vtx-card">
        <div class="vtx-history-header">
          <div>
            <h2 class="vtx-card-title">Storico file caricati</h2>
            <p class="vtx-history-count">{{ historyCountLabel }}</p>
          </div>
          <div class="vtx-date-filter">
            <label for="vtx-date" class="vtx-date-label">Giorno</label>
            <input id="vtx-date" v-model="selectedDate" type="date" class="vtx-date-input" :max="todayIso" />
          </div>
        </div>

        <div v-if="historyLoading" class="vtx-history-loading">Caricamento storico...</div>

        <div v-else-if="uploadedFiles.length > 0" class="vtx-table-wrap">
          <table class="vtx-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Data di caricamento</th>
                <th>Utente</th>
                <th>Scarica</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="file in uploadedFiles" :key="file.file_name">
                <td class="vtx-table-file">
                  <i class="pi pi-file"></i>
                  <span>{{ file.file_name }}</span>
                </td>
                <td>{{ file.created_at }}</td>
                <td>{{ file.email }}</td>
                <td>
                  <button type="button" class="vtx-download-btn" aria-label="Scarica file"
                    @click.stop.prevent="handleFileDownload(file.file_name)">
                    <i class="pi pi-download"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="vtx-empty">
          <i class="pi pi-inbox vtx-empty-icon"></i>
          <p>Nessun file caricato in questa data.</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { ref, computed, getCurrentInstance, onMounted, watch } from "vue";
import Service from "@/services/ask-caricamento-file/caricamento-file";
import Loader from "@/components/Loader.vue";
import { useToast } from "primevue/usetoast";
import moment from "moment";

export default {
  name: "CaricamentoVT4",
  components: {
    Loader,
  },

  setup() {
    const toast = useToast();
    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    const service = new Service(self.$eventBus);

    const category = ref("EE");
    const loading = ref(false);
    const historyLoading = ref(false);
    const submitted = ref(false);
    const validationErrors = ref({});
    const isDragging = ref(false);
    const fileToUpload = ref([]);
    const uploadedFiles = ref([]);

    const todayIso = moment().format("YYYY-MM-DD");
    const selectedDate = ref(todayIso);

    const allowedType = ["csv", "xlsx"];
    const acceptedFileTypes =
      ".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const maxSize = 500000 * 1024;

    const pageTitle = computed(() =>
      category.value === "EE" ? "Caricamento File VT4" : "Caricamento File VTG4"
    );
    const pageSubtitle = computed(() =>
      category.value === "EE"
        ? "Flussi elettrici, POD"
        : "Flussi gas, PDR"
    );

    const historyCountLabel = computed(() => {
      const count = uploadedFiles.value.length;
      const formattedDate = moment(selectedDate.value).format("DD/MM/YYYY");
      return count === 1
        ? `1 file trovato per il ${formattedDate}`
        : `${count} file trovati per il ${formattedDate}`;
    });

    const setCategory = (value) => {
      if (category.value === value) return;
      category.value = value;
      fileToUpload.value = [];
      submitted.value = false;
      validationErrors.value = {};
      fetchUploadedFiles();
    };

    const validateFiles = (files) => {
      const validFiles = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (file.size > maxSize) {
          toast.add({
            severity: "error",
            summary: "Errore",
            detail: `Il file ${file.name} supera la dimensione massima consentita (500 MB)`,
            life: 3000,
          });
          continue;
        }

        const extension = file.name.split(".").pop().toLowerCase();
        if (!allowedType.includes(extension)) {
          toast.add({
            severity: "error",
            summary: "Errore",
            detail: `Il file ${file.name} ha un formato non consentito. Formati ammessi: ${allowedType.join(", ")}`,
            life: 3000,
          });
          continue;
        }

        validFiles.push(file);
      }
      return validFiles;
    };

    const handleFileUpload = (evt) => {
      evt.preventDefault();
      submitted.value = false;
      fileToUpload.value = validateFiles(self.refs.fileInput.files);
    };

    const onDragOver = () => {
      isDragging.value = true;
    };

    const onDragLeave = () => {
      isDragging.value = false;
    };

    const onDrop = (evt) => {
      isDragging.value = false;
      submitted.value = false;
      fileToUpload.value = validateFiles(evt.dataTransfer.files);
    };

    const removeFile = (index) => {
      fileToUpload.value = fileToUpload.value.filter((_, i) => i !== index);
    };

    const formatFileSize = (size) => {
      const units = ["B", "KB", "MB", "GB"];
      let i = 0;
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i++;
      }
      return `${size.toFixed(1)} ${units[i]}`;
    };

    const validateForm = () => {
      if (fileToUpload.value.length === 0) {
        validationErrors.value["files"] = true;
        delete validationErrors.value["type"];
      } else {
        delete validationErrors.value["files"];
      }

      if (fileToUpload.value.length > 0) {
        const valid = fileToUpload.value.every((file) => {
          const extension = file.name.split(".").pop().toLowerCase();
          return allowedType.includes(extension);
        });
        if (valid) delete validationErrors.value["type"];
        else validationErrors.value["type"] = true;
      }

      return !Object.keys(validationErrors.value).length;
    };

    const uploadFile = () => {
      submitted.value = true;
      if (!validateForm()) return;

      loading.value = true;
      const upload =
        category.value === "EE"
          ? service.uploadFileVT4OnFtp(fileToUpload.value)
          : service.uploadFileVTG4OnFtp(fileToUpload.value);

      upload
        .then((response) => {
          const message = response.message || "File caricati con successo";
          toast.add({
            severity: "success",
            summary: "Successo",
            detail: message,
            life: 3000,
          });
          fileToUpload.value = [];
          submitted.value = false;
          fetchUploadedFiles();
        })
        .catch(() => {
          toast.add({
            severity: "error",
            summary: "Errore",
            detail: "Errore durante il caricamento dei file",
            life: 3000,
          });
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const fetchUploadedFiles = async () => {
      historyLoading.value = true;
      try {
        const filesResponse = await service.getAllUploadedFilesVT(selectedDate.value);

        uploadedFiles.value = filesResponse
          .filter((file) =>
            category.value === "GAS"
              ? file.name_fornitore === "PDR"
              : file.name_fornitore === "POD" || !file.name_fornitore
          )
          .map((file) => ({
            ...file,
            created_at: moment(file.created_at).format("YYYY-MM-DD HH:mm:ss"),
          }));
      } catch (error) {
        console.error("Errore durante il recupero dei file:", error);
        toast.add({
          severity: "error",
          summary: "Errore",
          detail: "Errore durante il recupero dello storico file",
          life: 3000,
        });
      } finally {
        historyLoading.value = false;
      }
    };

    const handleFileDownload = async (filename) => {
      try {
        const response =
          category.value === "EE"
            ? await service.downloadVt4(filename)
            : await service.downloadVtg4(filename);

        const blob = new Blob([response.data], { type: "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Errore durante il download:", error);
        let errorMessage = "Errore durante il download del file";
        if (error.response && error.response.status === 404) {
          errorMessage = "File non trovato";
        } else if (error.response && error.response.status === 500) {
          errorMessage = "Errore interno del server";
        }
        toast.add({
          severity: "error",
          summary: "Errore",
          detail: errorMessage,
          life: 3000,
        });
      }
    };

    onMounted(fetchUploadedFiles);
    watch(selectedDate, () => {
      if (!selectedDate.value) {
        selectedDate.value = todayIso;
        return;
      }
      fetchUploadedFiles();
    });

    return {
      category,
      loading,
      historyLoading,
      submitted,
      validationErrors,
      isDragging,
      fileToUpload,
      uploadedFiles,
      selectedDate,
      todayIso,
      allowedType,
      acceptedFileTypes,
      pageTitle,
      pageSubtitle,
      historyCountLabel,
      setCategory,
      handleFileUpload,
      onDragOver,
      onDragLeave,
      onDrop,
      removeFile,
      formatFileSize,
      uploadFile,
      handleFileDownload,
    };
  },
};
</script>

<style scoped>
.vtx-page {
  --vtx-ink: #0f172a;
  --vtx-ink-soft: #475569;
  --vtx-ink-faint: #94a3b8;
  --vtx-line: #e2e8f0;
  --vtx-surface: #ffffff;
  --vtx-canvas: #f8fafc;
  --vtx-ee: #2563eb;
  --vtx-ee-soft: #eff6ff;
  --vtx-gas: #d97706;
  --vtx-gas-soft: #fffbeb;
  --vtx-radius: 10px;

  width: 100% !important;
  min-height: 100%;
  background: var(--vtx-canvas) !important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  padding: 2rem 1rem !important;
  box-sizing: border-box !important;
}

.vtx-page *,
.vtx-page *::before,
.vtx-page *::after {
  box-sizing: border-box !important;
}

.vtx-container {
  max-width: 760px !important;
  margin: 0 auto !important;
}

/* Category switch */
.vtx-switch-row {
  display: flex !important;
  justify-content: center !important;
  margin-bottom: 1.5rem !important;
}

.vtx-switch {
  position: relative !important;
  display: inline-flex !important;
  background: var(--vtx-surface) !important;
  border: 1px solid var(--vtx-line) !important;
  border-radius: 999px !important;
  padding: 4px !important;
  gap: 2px !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04) !important;
}

.vtx-switch-indicator {
  position: absolute !important;
  top: 4px !important;
  left: 4px !important;
  width: calc(50% - 4px) !important;
  height: calc(100% - 8px) !important;
  background: var(--vtx-ee) !important;
  border-radius: 999px !important;
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), background-color 0.25s ease !important;
  z-index: 0 !important;
}

.vtx-switch-indicator--gas {
  transform: translateX(100%) !important;
  background: var(--vtx-gas) !important;
}

.vtx-switch-btn {
  position: relative !important;
  z-index: 1 !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 0.4rem !important;
  border: none !important;
  background: transparent !important;
  color: var(--vtx-ink-soft) !important;
  font-weight: 600 !important;
  font-size: 0.85rem !important;
  letter-spacing: 0.02em !important;
  padding: 0.5rem 1.25rem !important;
  border-radius: 999px !important;
  cursor: pointer !important;
  transition: color 0.2s ease !important;
}

.vtx-switch-btn--active {
  color: #ffffff !important;
}

.vtx-switch-btn:focus-visible {
  outline: 2px solid var(--vtx-ee) !important;
  outline-offset: 2px !important;
}

/* Header */
.vtx-header {
  text-align: center !important;
  margin-bottom: 2rem !important;
}

.vtx-title {
  font-size: 1.6rem !important;
  font-weight: 700 !important;
  color: var(--vtx-ink) !important;
  margin: 0 0 0.35rem 0 !important;
  letter-spacing: -0.01em !important;
}

.vtx-subtitle {
  font-size: 0.9rem !important;
  color: var(--vtx-ink-faint) !important;
  margin: 0 !important;
}

/* Card */
.vtx-card {
  background: var(--vtx-surface) !important;
  border: 1px solid var(--vtx-line) !important;
  border-radius: var(--vtx-radius) !important;
  padding: 1.75rem !important;
  margin-bottom: 1.5rem !important;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04) !important;
}

.vtx-card-title {
  font-size: 1rem !important;
  font-weight: 600 !important;
  color: var(--vtx-ink) !important;
  margin: 0 0 1rem 0 !important;
}

/* Dropzone */
.vtx-dropzone {
  position: relative !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.4rem !important;
  border: 1.5px dashed var(--vtx-line) !important;
  border-radius: var(--vtx-radius) !important;
  padding: 2.25rem 1.5rem !important;
  text-align: center !important;
  background: var(--vtx-canvas) !important;
  cursor: pointer !important;
  transition: border-color 0.2s ease, background-color 0.2s ease !important;
}

.vtx-dropzone:hover {
  border-color: var(--vtx-ee) !important;
  background: var(--vtx-ee-soft) !important;
}

.vtx-dropzone--drag {
  border-color: var(--vtx-ee) !important;
  background: var(--vtx-ee-soft) !important;
}

.vtx-dropzone-input {
  position: absolute !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
  opacity: 0 !important;
  cursor: pointer !important;
}

.vtx-dropzone-icon {
  font-size: 1.8rem !important;
  color: var(--vtx-ink-faint) !important;
}

.vtx-dropzone-text {
  font-size: 0.9rem !important;
  font-weight: 600 !important;
  color: var(--vtx-ink) !important;
}

.vtx-dropzone-hint {
  font-size: 0.78rem !important;
  color: var(--vtx-ink-faint) !important;
}

.vtx-error {
  color: #dc2626 !important;
  font-size: 0.8rem !important;
  margin-top: 0.5rem !important;
}

/* File list */
.vtx-file-list {
  list-style: none !important;
  margin: 1rem 0 0 0 !important;
  padding: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 0.4rem !important;
}

.vtx-file-item {
  display: flex !important;
  align-items: center !important;
  gap: 0.6rem !important;
  padding: 0.5rem 0.75rem !important;
  background: var(--vtx-canvas) !important;
  border: 1px solid var(--vtx-line) !important;
  border-radius: 8px !important;
  font-size: 0.85rem !important;
}

.vtx-file-item .pi-file {
  color: var(--vtx-ink-faint) !important;
}

.vtx-file-name {
  flex: 1 !important;
  color: var(--vtx-ink) !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

.vtx-file-size {
  color: var(--vtx-ink-faint) !important;
  font-size: 0.78rem !important;
}

.vtx-file-remove {
  border: none !important;
  background: transparent !important;
  color: var(--vtx-ink-faint) !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 22px !important;
  height: 22px !important;
  border-radius: 50% !important;
  transition: background-color 0.15s ease, color 0.15s ease !important;
}

.vtx-file-remove:hover {
  background: #fee2e2 !important;
  color: #dc2626 !important;
}

/* Actions */
.vtx-actions {
  display: flex !important;
  justify-content: center !important;
  margin-top: 1.25rem !important;
}

.vtx-btn {
  display: inline-flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  border: none !important;
  background: var(--vtx-ee) !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  font-size: 0.85rem !important;
  padding: 0.65rem 1.5rem !important;
  border-radius: 8px !important;
  cursor: pointer !important;
  transition: transform 0.15s ease, background-color 0.15s ease !important;
}

.vtx-btn:hover:not(:disabled) {
  transform: translateY(-1px) !important;
}

.vtx-btn:active:not(:disabled) {
  transform: translateY(0) scale(0.98) !important;
}

.vtx-btn:disabled {
  opacity: 0.55 !important;
  cursor: not-allowed !important;
}

.vtx-btn--gas {
  background: var(--vtx-gas) !important;
}

/* History header */
.vtx-history-header {
  display: flex !important;
  align-items: flex-start !important;
  justify-content: space-between !important;
  gap: 1rem !important;
  flex-wrap: wrap !important;
  margin-bottom: 1.25rem !important;
}

.vtx-history-count {
  font-size: 0.8rem !important;
  color: var(--vtx-ink-faint) !important;
  margin: 0.15rem 0 0 0 !important;
}

.vtx-date-filter {
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
}

.vtx-date-label {
  font-size: 0.8rem !important;
  font-weight: 600 !important;
  color: var(--vtx-ink-soft) !important;
}

.vtx-date-input {
  border: 1px solid var(--vtx-line) !important;
  border-radius: 8px !important;
  padding: 0.4rem 0.6rem !important;
  font-size: 0.85rem !important;
  color: var(--vtx-ink) !important;
  background: var(--vtx-surface) !important;
}

.vtx-date-input:focus {
  outline: none !important;
  border-color: var(--vtx-ee) !important;
  box-shadow: 0 0 0 3px var(--vtx-ee-soft) !important;
}

/* Table */
.vtx-table-wrap {
  overflow-x: auto !important;
  border: 1px solid var(--vtx-line) !important;
  border-radius: 8px !important;
}

.vtx-table {
  width: 100% !important;
  border-collapse: collapse !important;
  font-size: 0.85rem !important;
}

.vtx-table th {
  text-align: left !important;
  font-weight: 600 !important;
  color: var(--vtx-ink-soft) !important;
  background: var(--vtx-canvas) !important;
  padding: 0.65rem 0.9rem !important;
  border-bottom: 1px solid var(--vtx-line) !important;
}

.vtx-table td {
  padding: 0.65rem 0.9rem !important;
  border-bottom: 1px solid var(--vtx-line) !important;
  color: var(--vtx-ink) !important;
}

.vtx-table tr:last-child td {
  border-bottom: none !important;
}

.vtx-table tr:hover td {
  background: var(--vtx-canvas) !important;
}

.vtx-table-file {
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
}

.vtx-table-file .pi-file {
  color: var(--vtx-ink-faint) !important;
}

.vtx-download-btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 30px !important;
  height: 30px !important;
  border-radius: 6px !important;
  border: 1px solid var(--vtx-line) !important;
  background: var(--vtx-surface) !important;
  color: var(--vtx-ink-soft) !important;
  cursor: pointer !important;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease !important;
}

.vtx-download-btn:hover {
  background: var(--vtx-ee) !important;
  border-color: var(--vtx-ee) !important;
  color: #ffffff !important;
}

/* History loading */
.vtx-history-loading {
  text-align: center !important;
  padding: 2.5rem 1rem !important;
  color: var(--vtx-ink-faint) !important;
  font-size: 0.85rem !important;
}

/* Empty state */
.vtx-empty {
  text-align: center !important;
  padding: 2.5rem 1rem !important;
  color: var(--vtx-ink-faint) !important;
}

.vtx-empty-icon {
  font-size: 1.8rem !important;
  margin-bottom: 0.5rem !important;
  display: block !important;
}

.vtx-empty p {
  margin: 0 !important;
  font-size: 0.85rem !important;
}

@media (max-width: 600px) {
  .vtx-card {
    padding: 1.25rem !important;
  }

  .vtx-history-header {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .vtx-date-filter {
    justify-content: space-between !important;
  }
}
</style>
